import React from 'react'
import { BetCard } from '@mobi/betslip/Components/Common/BetCard'
import { BetCardTitle } from '@mobi/betslip/Components/Common/BetCardTitle'
import { spacing } from '@mobi/component-library/Foundations'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { RadioButton } from '@mobi/component-library/Selection&Input/RadioButton'
import { SportsIcon } from '@mobi/sports-offering/Components/Common/SportsIcon'
import { TeamsIcon } from '@mobi/sports-offering/Components/Common/TeamsIcon'
import { formatKambiCurrencyToDollarDecimal } from '@mobi/sports-offering/helpers/currency/currencyFormat'
import { toMoney } from '@mobi/utils/money'
import type { BetLoopKambiShareCoupon } from '@core/Areas/BetLoop/types'
import { KAMBI_PENDING_HASH_ROUTE } from '@core/Areas/KambiClient/constants'
import { getEventLinkForKambiClient } from '@core/Areas/KambiClient/helpers/navigation'
import { useNewSportsOfferingFeature } from '@core/Areas/KambiSports/Offering/hooks/useNewSportsOfferingFeature'
import { CountdownStatus } from '@core/Areas/KambiSports/Player/Components/Coupon/Components/Common/CountdownStatus'
import { LegStatusIcon } from '@core/Areas/KambiSports/Player/Components/Coupon/Components/Common/LegStatusIcon'
import { LegsProgressStatus } from '@core/Areas/KambiSports/Player/Components/Coupon/Components/Common/LegsProgressStatus'
import { KambiHistoryCouponDisplayContext } from '@core/Areas/KambiSports/Player/Components/Coupon/context'
import { shouldProvideLinkToEvent } from '@core/Areas/KambiSports/Player/helpers/shouldProvideLinkToEvent'

export const SameGameMulti: React.FC<{
  legCount: number
  event: BetLoopKambiShareCoupon['events'][0]
  outcomes: BetLoopKambiShareCoupon['outcomes']
  betOffers: BetLoopKambiShareCoupon['betOffers']
  playedOdds: BetLoopKambiShareCoupon['couponRows'][0]['playedOdds']
  betStatus:
    | BetLoopKambiShareCoupon['bets'][0]['betStatus']
    | BetLoopKambiShareCoupon['couponRows'][0]['status']
  isWithinMultiBet?: boolean
  onMultiLegCountdownReachingZero?(): void
}> = ({
  legCount,
  event,
  outcomes,
  betOffers,
  playedOdds,
  betStatus,
  isWithinMultiBet,
  onMultiLegCountdownReachingZero,
}) => {
  const instanceId = React.useId()
  const couponDisplayContext = React.useContext(KambiHistoryCouponDisplayContext)
  const isNewSportsUiEnabled = useNewSportsOfferingFeature()

  const isBetLoopQuickShare = couponDisplayContext.type === 'betloop-quick-share'
  const isBetHistoryCoupon = couponDisplayContext.type === 'bet-history'

  const titleText = `${legCount} Leg Same Game Multi`
  const odds = playedOdds && toMoney(formatKambiCurrencyToDollarDecimal(playedOdds))
  const sportName = event.sport
  const hasOpenOutcomes = outcomes.some(outcome => outcome.status === 'OPEN')

  return (
    <>
      {!isBetHistoryCoupon && (
        <BetCardTitle
          title={titleText}
          rightComponent={
            <Flex gap={spacing.smx2}>
              {odds}

              {isBetLoopQuickShare && (
                <RadioButton
                  size='md'
                  type='Tick'
                  id={instanceId}
                  isChecked={couponDisplayContext.data.isSelected}
                />
              )}
            </Flex>
          }
          bottomLeftComponent={
            <LegsProgressStatus outcomes={outcomes} betOffers={betOffers} events={[event]} />
          }
        />
      )}

      {isBetHistoryCoupon && !isWithinMultiBet ? null : (
        <BetCard.EventDetails
          iconComponent={<SportsIcon sport={sportName} groupId={event.eventGroups[0].id} />}
          heading={event.eventName}
          statusComponent={
            ((hasOpenOutcomes && isWithinMultiBet) || !isWithinMultiBet) && (
              <CountdownStatus
                displayType={isWithinMultiBet ? 'multi-leg' : 'standard'}
                eventStartTime={event.eventStartDate}
                betStatus={betStatus}
                onCountdownReachingZero={onMultiLegCountdownReachingZero}
              />
            )
          }
        />
      )}

      {outcomes.map(outcome => {
        const betOffer = betOffers.find(b => b.betOfferId === outcome.betOfferId)
        if (!betOffer) return null

        const shouldEnableLinkToEvent = shouldProvideLinkToEvent({
          outcomeStatus: outcome.status,
          eventStartTime: event.eventStartDate,
        })

        const linkInfo = isBetHistoryCoupon
          ? { pathname: KAMBI_PENDING_HASH_ROUTE }
          : shouldEnableLinkToEvent
            ? { pathname: getEventLinkForKambiClient(event.eventId, isNewSportsUiEnabled) }
            : undefined

        return (
          <BetCard.SelectionInfo
            key={outcome.betOfferId}
            // eslint-disable-next-line react/jsx-no-useless-fragment
            heading={<>{outcome.label}</>}
            subHeading={betOffer.criterion}
            linkInfo={linkInfo}
            rightAlignedComponent={<LegStatusIcon status={outcome.status} />}
            iconComponent={
              <TeamsIcon
                size='sm'
                participantId={outcome.participantId ?? null}
                betOfferType={betOffer.boTypeId}
                betOfferId={outcome.betOfferId}
                outcomeId={outcome.outcomeId}
                sportName={sportName}
              />
            }
          />
        )
      })}
    </>
  )
}
